import React, { useMemo } from "react";
import { Field } from "react-final-form";
import { TextField } from "@mui/material";
import RffInputWrapper from "../shared/RffInputWrapper";

/**
 * RffNumberField — a plain count/quantity input. Stores a Number, not the string
 * a bare text input would leave behind, and displays it with thousands
 * separators.
 */
const RffNumberField = ({
  name,
  label,
  required = false,
  placeholder = "Enter number",
  helperText,
  fullWidth = true,
  row = false,
  suppressGrid,
  size,
  disabled,
  onBlur,
  min,
  max,
}) => {
  const validate = useMemo(
    () => (value) => {
      if (required && (value === undefined || value === null || value === "")) {
        return "This field is required";
      }
      if (min !== undefined && value < min) return `${label} must be at least ${min}`;
      if (max !== undefined && value > max) return `${label} must be at most ${max}`;
      return undefined;
    },
    [required, min, max, label],
  );

  return (
    <RffInputWrapper
      label={label}
      suppressGrid={suppressGrid}
      size={size}
      helperText={helperText}
      required={required}
      row={row}
    >
      <Field
        name={name}
        parse={(v) => (v === undefined || v === null || v === "" ? undefined : Number(String(v).replace(/,/g, "")))}
        format={(v) =>
          v === undefined || v === null || v === ""
            ? ""
            : new Intl.NumberFormat().format(v)
        }
        validate={validate}
        render={({ input, meta }) => (
          <TextField
            {...input}
            onBlur={onBlur || input.onBlur}
            placeholder={placeholder}
            disabled={disabled}
            fullWidth={fullWidth}
            // "tel" rather than "number": it brings up a numeric keypad without
            // the scroll-to-change behaviour a number input has on desktop.
            type="tel"
            inputMode="numeric"
            inputProps={{ min, max, pattern: "[0-9]*" }}
            error={meta.touched && !!meta.error}
            helperText={meta.touched && meta.error ? meta.error : null}
          />
        )}
      />
    </RffInputWrapper>
  );
};

export default RffNumberField;
